/**
 * Custom hook: useBidHandlers
 * Contains large handler functions used by NC/MAS Direct/MAS Indirect template core hooks.
 * for size reduction. These handlers are bound to the component state via deps.
 */
import React, { useRef } from 'react';
import { toast } from 'react-toastify';
import {
  preserveGlobalFields as preserveGlobalFieldsLogic,
  parseDateString,
  formatDateForChartAPI,
  formatDateForAPI,
} from '../bidAnalysisTemplate.logic';
import { whlsFeeCalculationNC } from '../ncBlendedWhlsFeeCalculator';
import { calculateGSMSNetCostFromMfg } from '../bidAnalysisFormulas';

export interface UseBidHandlersDeps {
  // State values
  formData: any;
  yearFormData: Record<number, Record<number, any>>;
  packSizeFormData: Record<number, any>;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  bidDetails: any;
  templateType: string;
  productType: string | null;
  supplierName: string;
  contractsList: any[];
  contractPriceRanking: any[];
  ndcValidated: boolean;
  selectedConcept: string | null;
  analysisType: string;
  gsmsHndlgType: string;
  allPackSizeTabs: any[];
  packSizeTabs: any[];
  templateData: any;
  chartDateRange: { startDate: Date | null; endDate: Date | null };
  selectedCompany: string | null;
  bidId: string | undefined;
  // Refs
  yearFormDataRef: React.MutableRefObject<Record<number, Record<number, any>>>;
  isInitialLoadRef: React.MutableRefObject<boolean>;
  hasUserMadeChangesRef: React.MutableRefObject<boolean>;
  contractPriceRankingAbortControllerRef: React.MutableRefObject<AbortController | null>;
  marketSalesTrendAbortControllerRef: React.MutableRefObject<AbortController | null>;
  copyAllYearsDataRef: React.MutableRefObject<any>;
  // Setters
  setFormData: (updater: any) => void;
  setYearFormData: (updater: any) => void;
  setPackSizeFormData: (updater: any) => void;
  setValidationErrors: (updater: any) => void;
  setNdcValidated: (v: boolean) => void;
  setIsValidatingNdc: (v: boolean) => void;
  setIsLoadingNdcData: (v: boolean) => void;
  setNdcOptions: (v: any[]) => void;
  setActivePackSizeTab: (v: number) => void;
  setActiveYearTab: (v: number) => void;
  setSelectedCompany: (v: string | null) => void;
  setChartDateRange: (range: { startDate: Date | null; endDate: Date | null }) => void;
  setCalendarViewDates: (dates: any) => void;
  handleFieldChange: (field: string, value: any) => void;
  wasGsmsProfitManuallySet: boolean;
  setWasGsmsProfitManuallySet: (v: boolean) => void;
  packSizeFormDataRef: React.MutableRefObject<Record<number, any>>;
  setContractPriceRanking: (data: any[]) => void;
  setFilteredMarketSalesTrendData: (data: any[]) => void;
  setMarketSalesTrendData: (data: any[]) => void;
  setMarketShareByVolume: (data: any) => void;
  // Functions
  recalculateFields: (paramsOrUpdatedData: any, changedField?: string) => Record<string, any>;
  loadContractPriceRanking: (
    startDate?: string,
    endDate?: string,
    forceRefresh?: boolean,
    limit?: number
  ) => void;
  loadNdcOptions: (
    supplier: string,
    concept: string,
    packSize: number,
    forceReload?: boolean
  ) => Promise<void>;
  loadMarketSalesTrendData: (
    company: string | null,
    forceRefresh?: boolean,
    startDate?: Date | null,
    endDate?: Date | null
  ) => Promise<void>;
  loadMarketShareByVolume: (
    forceRefresh?: boolean,
    startDate?: Date | null,
    endDate?: Date | null
  ) => Promise<void>;
  loadVolumeByCot: (
    labeller?: string | null,
    customStartDate?: Date | string | null,
    customEndDate?: Date | string | null
  ) => Promise<void>;
  // Stores
  BidAnalysisStore: any;
  // Misc
  validateField: (fieldName: string, value: any) => string | null;
  formatTargetLaunchDateForDisplay: (date: any, yearOffset?: number) => string;
}

export function useBidHandlers(deps: UseBidHandlersDeps) {
  const {
    formData,
    yearFormData,
    packSizeFormData,
    activePackSizeTab,
    activeYearTab,
    bidDetails,
    templateType,
    productType,
    supplierName,
    contractsList: _contractsList,
    contractPriceRanking,
    ndcValidated: _ndcValidated,
    selectedConcept,
    analysisType,
    gsmsHndlgType,
    allPackSizeTabs,
    packSizeTabs,
    templateData,
    chartDateRange,
    selectedCompany,
    bidId,
    yearFormDataRef,
    isInitialLoadRef: _isInitialLoadRef,
    hasUserMadeChangesRef,
    contractPriceRankingAbortControllerRef,
    marketSalesTrendAbortControllerRef: _marketSalesTrendAbortControllerRef,
    copyAllYearsDataRef,
    setFormData,
    setYearFormData,
    setPackSizeFormData,
    setValidationErrors,
    setNdcValidated: _setNdcValidated,
    setIsValidatingNdc: _setIsValidatingNdc,
    setIsLoadingNdcData: _setIsLoadingNdcData,
    setNdcOptions,
    setActivePackSizeTab,
    setActiveYearTab,
    setSelectedCompany,
    recalculateFields,
    loadContractPriceRanking,
    loadNdcOptions,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    loadVolumeByCot,
    BidAnalysisStore,
    validateField,
    formatTargetLaunchDateForDisplay: _formatTargetLaunchDateForDisplay,
    setChartDateRange,
    setCalendarViewDates,
    handleFieldChange,
    wasGsmsProfitManuallySet,
    setWasGsmsProfitManuallySet: _setWasGsmsProfitManuallySet,
    packSizeFormDataRef,
    setContractPriceRanking,
    setFilteredMarketSalesTrendData,
    setMarketSalesTrendData,
    setMarketShareByVolume,
  } = deps;

  const preserveGlobalFields = (newData: any): any => preserveGlobalFieldsLogic(newData, formData);

  const isTabChangingRef = useRef(false);
  const lastTabFieldRef = useRef<string | null>(null);

  const handleFieldBlur = async (fieldName: string, event?: any) => {
    // Validation for target launch date is already handled in handleFieldChange
    // which runs immediately when the date changes, so we don't need to validate again here
    // The validation in handleFieldChange uses the updated formData values correctly

    // Handle usage period dates separately - they need chart updates but not field recalculation
    // This must be done early to use the event value directly from calendar picker
    if (fieldName === 'usagePeriodStartDate' || fieldName === 'usagePeriodEndDate') {
      // Get the date value from event (calendar picker passes Date object) or formData
      const dateValue =
        event instanceof Date
          ? event
          : event !== undefined && event !== null && typeof event !== 'object'
            ? event
            : formData[fieldName];

      if (!dateValue) {
        return; // No date value, skip
      }

      // Check if this is NC template
      const currentTemplateType = bidDetails?.template_type || 'mas';
      const isNC = currentTemplateType === 'nc';

      if (isNC) {
        // NC template: Update yearFormData
        // Access nested structure: yearFormData[activePackSizeTab as number][activeYearTab as number]
        const currentYearData =
          activePackSizeTab !== null
            ? yearFormData[activePackSizeTab as number]?.[activeYearTab as number] || {}
            : {};
        const updatedYearData = {
          ...currentYearData,
          [fieldName]: dateValue,
        };

        setYearFormData((prev: Record<number, Record<number, any>>) => {
          if (activePackSizeTab === null) return prev;
          return {
            ...prev,
            [activePackSizeTab as number]: {
              ...prev[activePackSizeTab as number],
              [activeYearTab as number]: updatedYearData,
            },
          };
        });

        // Update ref immediately
        if (activePackSizeTab !== null) {
          yearFormDataRef.current = {
            ...yearFormDataRef.current,
            [activePackSizeTab as number]: {
              ...yearFormDataRef.current[activePackSizeTab as number],
              [activeYearTab as number]: updatedYearData,
            },
          };
        }

        // Update formData
        setFormData((prev: any) => ({
          ...prev,
          [fieldName]: dateValue,
        }));

        // Mark that user has made changes
        hasUserMadeChangesRef.current = true;

        // Get both dates for chart update
        const startDate =
          fieldName === 'usagePeriodStartDate'
            ? dateValue
            : updatedYearData.usagePeriodStartDate || formData.usagePeriodStartDate;
        const endDate =
          fieldName === 'usagePeriodEndDate'
            ? dateValue
            : updatedYearData.usagePeriodEndDate || formData.usagePeriodEndDate;

        // Validate that end date is not before start date before updating chart
        if (startDate && endDate) {
          try {
            const parsedStart = startDate instanceof Date ? startDate : parseDateString(startDate);
            const parsedEnd = endDate instanceof Date ? endDate : parseDateString(endDate);

            if (
              parsedStart &&
              parsedEnd &&
              !isNaN(parsedStart.getTime()) &&
              !isNaN(parsedEnd.getTime())
            ) {
              // Validate date range
              if (parsedEnd.getTime() < parsedStart.getTime()) {
                // Set validation error and don't update chart or load graphs
                setValidationErrors(prev => ({
                  ...prev,
                  usagePeriodEndDate: 'End date must be after start date',
                }));
                return; // Don't update chart dates or load graphs
              }

              // Clear validation error if dates are valid
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.usagePeriodEndDate;
                return newErrors;
              });

              // Check if user has applied custom dates - don't override them
              const conceptName = updatedYearData.conceptName || formData.conceptName;
              const packSize = updatedYearData.packSize || formData.packSize;
              const bidIdNum = bidId ? parseInt(bidId) : null;
              const hasCustomDates =
                bidIdNum && conceptName && packSize
                  ? BidAnalysisStore.getChartDateRange(
                      bidIdNum,
                      conceptName,
                      parseFloat(packSize)
                    ) !== undefined
                  : false;

              // Only update chart date range if no custom dates have been applied
              if (!hasCustomDates) {
                // Update chart date range immediately
                setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
                setCalendarViewDates({ startViewDate: parsedStart, endViewDate: parsedEnd });
              }

              // Reload chart data with new dates (always reload, but use custom dates if they exist)
              if (conceptName && packSize && bidId) {
                const parsedStartDate =
                  parsedStart instanceof Date ? parsedStart : new Date(parsedStart);
                const parsedEndDate = parsedEnd instanceof Date ? parsedEnd : new Date(parsedEnd);
                const _startDateStr = formatDateForAPI(startDate);
                const _endDateStr = formatDateForAPI(endDate);

                // Use custom dates if they exist, otherwise use usage period dates
                if (hasCustomDates) {
                  const customDateRange = BidAnalysisStore.getChartDateRange(
                    bidIdNum!,
                    conceptName,
                    parseFloat(packSize)
                  );
                  if (customDateRange?.startDate && customDateRange?.endDate) {
                    loadMarketSalesTrendData(
                      null,
                      true,
                      customDateRange.startDate,
                      customDateRange.endDate
                    );
                    loadMarketShareByVolume(
                      true,
                      customDateRange.startDate,
                      customDateRange.endDate
                    );
                    const customStartStr = formatDateForChartAPI(customDateRange.startDate);
                    const customEndStr = formatDateForChartAPI(customDateRange.endDate);
                    if (customStartStr && customEndStr) {
                      // Debounce API call to prevent cancellation on rapid date changes (500ms delay)
                      loadContractPriceRanking(customStartStr, customEndStr, true, 500);
                    }
                  }
                } else {
                  // Force reload by passing true for skipStore check
                  loadMarketSalesTrendData(null, true, parsedStartDate, parsedEndDate);
                  loadMarketShareByVolume(true, parsedStartDate, parsedEndDate);
                  // Format dates to yyyy-mm-dd for contract price ranking API
                  const contractRankingStartDate = formatDateForChartAPI(parsedStartDate);
                  const contractRankingEndDate = formatDateForChartAPI(parsedEndDate);
                  if (contractRankingStartDate && contractRankingEndDate) {
                    // Debounce API call to prevent cancellation on rapid date changes (500ms delay)
                    loadContractPriceRanking(
                      contractRankingStartDate,
                      contractRankingEndDate,
                      true,
                      500
                    );
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error updating chart date range from usage period:', error);
          }
        }
      } else {
        // MAS template: Update packSizeFormData
        const currentPackData = packSizeFormData[activePackSizeTab as number] || {};
        const updatedPackData = {
          ...currentPackData,
          [fieldName]: dateValue,
        };

        setPackSizeFormData((prev: Record<number, any>) => ({
          ...prev,
          [activePackSizeTab as number]: updatedPackData,
        }));

        // Update formData
        setFormData((prev: any) => ({
          ...prev,
          [fieldName]: dateValue,
        }));

        // Mark that user has made changes
        hasUserMadeChangesRef.current = true;

        // Get both dates for chart update
        const startDate =
          fieldName === 'usagePeriodStartDate'
            ? dateValue
            : updatedPackData.usagePeriodStartDate || formData.usagePeriodStartDate;
        const endDate =
          fieldName === 'usagePeriodEndDate'
            ? dateValue
            : updatedPackData.usagePeriodEndDate || formData.usagePeriodEndDate;

        // Update chart immediately if both dates are available
        if (startDate && endDate) {
          try {
            const parsedStart = startDate instanceof Date ? startDate : parseDateString(startDate);
            const parsedEnd = endDate instanceof Date ? endDate : parseDateString(endDate);

            if (
              parsedStart &&
              parsedEnd &&
              !isNaN(parsedStart.getTime()) &&
              !isNaN(parsedEnd.getTime())
            ) {
              // Check if user has applied custom dates - don't override them
              const conceptName = updatedPackData.conceptName || formData.conceptName;
              const packSize = updatedPackData.packSize || formData.packSize;
              const bidIdNum = bidId ? parseInt(bidId) : null;
              const hasCustomDates =
                bidIdNum && conceptName && packSize
                  ? BidAnalysisStore.getChartDateRange(
                      bidIdNum,
                      conceptName,
                      parseFloat(packSize)
                    ) !== undefined
                  : false;

              // Only update chart date range if no custom dates have been applied
              if (!hasCustomDates) {
                // Update chart date range immediately
                setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
                setCalendarViewDates({ startViewDate: parsedStart, endViewDate: parsedEnd });
              }

              // Reload chart data with new dates (always reload, but use custom dates if they exist)
              if (conceptName && packSize && bidId) {
                // Use custom dates if they exist, otherwise use usage period dates
                if (hasCustomDates) {
                  const customDateRange = BidAnalysisStore.getChartDateRange(
                    bidIdNum!,
                    conceptName,
                    parseFloat(packSize)
                  );
                  if (customDateRange?.startDate && customDateRange?.endDate) {
                    loadMarketSalesTrendData(
                      null,
                      true,
                      customDateRange.startDate,
                      customDateRange.endDate
                    );
                    loadMarketShareByVolume(
                      true,
                      customDateRange.startDate,
                      customDateRange.endDate
                    );
                    const customStartStr = formatDateForChartAPI(customDateRange.startDate);
                    const customEndStr = formatDateForChartAPI(customDateRange.endDate);
                    if (customStartStr && customEndStr) {
                      // Debounce API call to prevent cancellation on rapid date changes (500ms delay)
                      loadContractPriceRanking(customStartStr, customEndStr, true, 500);
                    }
                  }
                } else {
                  const parsedStartDate =
                    parsedStart instanceof Date ? parsedStart : new Date(parsedStart);
                  const parsedEndDate = parsedEnd instanceof Date ? parsedEnd : new Date(parsedEnd);
                  const _startDateStr2 = formatDateForAPI(startDate);
                  const _endDateStr2 = formatDateForAPI(endDate);

                  // Force reload by passing true for skipStore check
                  loadMarketSalesTrendData(null, true, parsedStartDate, parsedEndDate);
                  loadMarketShareByVolume(true, parsedStartDate, parsedEndDate);
                  // Format dates to yyyy-mm-dd for contract price ranking API
                  const contractRankingStartDate = formatDateForChartAPI(parsedStartDate);
                  const contractRankingEndDate = formatDateForChartAPI(parsedEndDate);
                  if (contractRankingStartDate && contractRankingEndDate) {
                    // Debounce API call to prevent cancellation on rapid date changes (500ms delay)
                    loadContractPriceRanking(
                      contractRankingStartDate,
                      contractRankingEndDate,
                      true,
                      500
                    );
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error updating chart date range from usage period:', error);
          }
        }
      }

      return; // Don't proceed with normal recalculation for usage period dates
    }

    // Skip recalculation for global fields that don't need it
    // Note: gsmsHndlgType is removed from this list because it needs to trigger recalculation
    // for dependent fields like bottle capacity and bottle cost
    const globalFieldsNoRecalc = [
      'analysisType',
      'supplierName',
      'analysisDate',
      'targetLaunchDate',
    ];
    if (globalFieldsNoRecalc.includes(fieldName)) {
      // These fields are already updated via handleFieldChange, no need to recalculate
      return;
    }

    // For InputNumber, the value is already in formData (updated by onValueChange)
    // We just need to use the current formData value
    // If event is a direct value (string/number), use it directly
    let value =
      typeof event !== 'object' || event === null
        ? event !== undefined
          ? event
          : formData[fieldName]
        : formData[fieldName];

    // Tracks whether gsmsCostFromMfg was blurred without actually changing its value.
    // When true, we still run recalc (to keep gsmsProfit% in sync) but we must NOT
    // force-recalculate blendedWhlsFee — that would overwrite the correct per-year fee
    // that copyForAllYears already computed for NC option years.
    let isGsmsCostNoChangeBlur = false;

    // If event is passed and it's from an InputNumber, try to extract the raw value
    if (event?.target) {
      const inputElement =
        event.target.tagName === 'INPUT' ? event.target : event.target.querySelector('input');

      if (inputElement) {
        const rawValue = inputElement.value;

        if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
          // Remove currency symbols, commas, and other formatting (but preserve minus sign for negative values)
          const cleanValue = rawValue.replace(/[$,\s%]/g, '');
          const parsedValue = parseFloat(cleanValue);
          if (!isNaN(parsedValue)) {
            // Get current value from form data to compare
            // For NC, use yearFormData; for MAS, use packSizeFormData
            const currentPackData =
              templateType === 'nc' && activePackSizeTab !== null
                ? yearFormData[activePackSizeTab as number]?.[activeYearTab as number] || {}
                : packSizeFormData[activePackSizeTab as number] || {};
            const currentValue = currentPackData[fieldName] ?? null;
            const currentNumValue =
              currentValue !== null && currentValue !== undefined
                ? typeof currentValue === 'number'
                  ? currentValue
                  : parseFloat(String(currentValue))
                : null;

            // Skip recalculation if the value hasn't changed
            // This prevents unnecessary recalculations when user just clicks and blurs without changing value
            // EXCEPTION: For gsmsProfit, if gsmsCostFromMfg is empty/null, we need to trigger goal seek
            // even if the profit value didn't change (to populate gsmsCostFromMfg)
            // EXCEPTION: For gsmsCostFromMfg/gsmsNetCostFromMfg, always run recalc on blur so GSMS profit % is updated every time
            let shouldSkipRecalc =
              currentNumValue !== null && Math.abs(currentNumValue - parsedValue) <= 0.001;
            // Tab key: the user intentionally moved off the field — always trigger recalculation
            if (lastTabFieldRef.current === fieldName) {
              lastTabFieldRef.current = null;
              shouldSkipRecalc = false;
            }
            if (shouldSkipRecalc) {
              // Always recalc when GSMS Cost From Mfg (or Net Cost From Mfg) is blurred so GSMS profit % stays updated
              if (fieldName === 'gsmsCostFromMfg' || fieldName === 'gsmsNetCostFromMfg') {
                value = parsedValue;
                // Mark that this is a no-value-change blur for gsmsCostFromMfg so that
                // recalcCore skips the forced blendedWhlsFee recalculation.  The correct
                // per-year fee was already set by copyForAllYears and must not be overwritten.
                if (fieldName === 'gsmsCostFromMfg') {
                  isGsmsCostNoChangeBlur = true;
                }
                // Fall through to recalculation below (don't return)
              } else if (fieldName === 'gsmsProfit') {
                // Check if this is gsmsProfit and gsmsCostFromMfg is empty/null - if so, trigger goal seek
                const currentPackData =
                  templateType === 'nc' && activePackSizeTab !== null
                    ? yearFormData[activePackSizeTab as number]?.[activeYearTab as number] || {}
                    : packSizeFormData[activePackSizeTab as number] || {};
                const gsmsCostFromMfg = currentPackData.gsmsCostFromMfg ?? formData.gsmsCostFromMfg;
                const isGsmsCostFromMfgEmpty =
                  gsmsCostFromMfg === null ||
                  gsmsCostFromMfg === undefined ||
                  gsmsCostFromMfg === '' ||
                  gsmsCostFromMfg === '0' ||
                  gsmsCostFromMfg === '0.00';
                const suggestedBidPrice =
                  currentPackData.suggestedBidPrice ?? formData.suggestedBidPrice;
                // If gsmsCostFromMfg is empty and suggestedBidPrice > 0, trigger goal seek to populate it
                if (
                  isGsmsCostFromMfgEmpty &&
                  suggestedBidPrice &&
                  parseFloat(String(suggestedBidPrice)) > 0
                ) {
                  // Don't return - continue to trigger goal seek
                  value = parsedValue; // Use the current profit value for goal seek
                } else {
                  // Value hasn't changed and no need for goal seek, don't trigger any recalculation
                  return;
                }
              } else {
                // Value hasn't changed, don't trigger any recalculation
                // This applies to suggestedBidPrice and other numeric fields
                return;
              }
            }
            // Value changed, proceed with recalculation
            value = parsedValue; // This will correctly handle negative values
            // Update formData with the extracted value first
            await new Promise(resolve => {
              handleFieldChange(fieldName, value);
              setTimeout(resolve, 0); // Allow state update
            });
            // Continue to recalculation below (don't return early)
          }
        } else {
          value = null;
        }
      }
    }

    // CRITICAL: For gsmsCostFromMfg, ensure null/empty values are properly handled
    // When field is cleared, explicitly set to null (not empty string) to ensure UI shows blank
    if (
      fieldName === 'gsmsCostFromMfg' &&
      (value === '' || value === null || value === undefined)
    ) {
      value = null;
      // Explicitly update the field to null to ensure UI reflects the cleared state
      handleFieldChange(fieldName, null);
    }

    // Validate the field
    const error = validateField(fieldName, value);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: error,
      }));
      // For suggestedBidPrice, continue with calculations even if validation fails
      if (fieldName !== 'suggestedBidPrice') {
        return; // Don't proceed with calculations if validation fails for other fields
      }
    } else {
      // Clear validation error if valid
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    // For GSMS Profit, ensure value is within valid range (-100 to 100)
    // Allow negative values for all analysis types (cost can exceed price)
    if (fieldName === 'gsmsProfit' && value !== null && value !== undefined) {
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (!isNaN(numValue)) {
        // Allow -100 to 100 for all analysis types
        if (numValue > 100) {
          value = 100;
          handleFieldChange(fieldName, 100);
        } else if (numValue < -100) {
          value = -100;
          handleFieldChange(fieldName, -100);
        }
        // No need to reset to 0 for negative values - they are valid
      }
    }

    // Update form data and recalculate
    // For NC, use yearFormData; for MAS, use packSizeFormData
    const currentTemplateType = bidDetails?.template_type || 'mas';
    // For NC: prefer the ref (always most up-to-date) over React state which may lag by one render.
    // This is critical for option years: if the user navigated away and back, state may still hold
    // stale base-year-influenced data while the ref has the correct option-year snapshot.
    const currentPackData =
      currentTemplateType === 'nc' && activePackSizeTab !== null
        ? yearFormDataRef.current[activePackSizeTab as number]?.[activeYearTab as number] ||
          yearFormData[activePackSizeTab as number]?.[activeYearTab as number] ||
          {}
        : packSizeFormData[activePackSizeTab as number] || {};

    // Ensure supplierDiscount and supplierCode are included
    // For NC templates: Use base year's supplierDiscount (same discount for all years)
    // For MAS templates: Use packSizeFormData
    // This is critical for Direct products where discount affects calculations
    // CRITICAL: Always check formData as well, since discount might be there
    let supplierDiscountFromPack: number | undefined;
    let supplierCodeFromPack: string | undefined;

    if (currentTemplateType === 'nc' && activePackSizeTab !== null) {
      // For NC templates, get supplierDiscount from base year (index 0)
      // Option years should use the same discount as base year
      const baseYearData = yearFormData[activePackSizeTab as number]?.[0] || {};
      supplierDiscountFromPack =
        baseYearData.supplierDiscount ??
        currentPackData.supplierDiscount ??
        formData.supplierDiscount;
      supplierCodeFromPack =
        baseYearData.supplierCode ?? currentPackData.supplierCode ?? formData.supplierCode;
    } else {
      // For MAS templates, use packSizeFormData, but also check formData
      const packSizeData = packSizeFormData[activePackSizeTab as number] || {};
      supplierDiscountFromPack = packSizeData.supplierDiscount ?? formData.supplierDiscount;
      supplierCodeFromPack = packSizeData.supplierCode ?? formData.supplierCode;
    }

    // For NC templates, ensure gsmsPackSize comes from the pack size tab (readonly field)
    // Get the pack size from the active tab
    const activeTab = packSizeTabs.find(tab => tab.index === activePackSizeTab);
    const packSizeFromTab = activeTab?.packSize ? parseFloat(activeTab.packSize) : null;

    // CRITICAL: For NC templates, ensure we use the current year's targetLaunchDate
    // This ensures blendedWhlsFee is calculated with the correct year-specific date
    // IMPORTANT: For base year (activeYearTab === 0), targetLaunchDate is global and comes from formData
    // For option years (activeYearTab > 0), targetLaunchDate is year-specific
    let currentYearTargetLaunchDate: string | Date | null | undefined;
    if (
      currentTemplateType === 'nc' &&
      activePackSizeTab !== null &&
      activeYearTab !== null &&
      activeYearTab !== 5
    ) {
      if (activeYearTab === 0) {
        // Base year: targetLaunchDate is global, always use formData.targetLaunchDate
        currentYearTargetLaunchDate = formData.targetLaunchDate;
      } else {
        // Option years (1-4): prioritize currentPackData.targetLaunchDate
        // If it's not set, calculate it from base year's date + yearIndex
        if (currentPackData.targetLaunchDate) {
          currentYearTargetLaunchDate = currentPackData.targetLaunchDate;
        } else if (formData.targetLaunchDate && activeYearTab > 0) {
          // Calculate year-specific date from base year's date
          try {
            const baseDate =
              typeof formData.targetLaunchDate === 'string'
                ? new Date(formData.targetLaunchDate)
                : formData.targetLaunchDate;
            if (baseDate instanceof Date && !isNaN(baseDate.getTime())) {
              const yearDate = new Date(baseDate);
              yearDate.setFullYear(baseDate.getFullYear() + activeYearTab);
              currentYearTargetLaunchDate = yearDate;
            } else {
              currentYearTargetLaunchDate = formData.targetLaunchDate;
            }
          } catch (_e) {
            currentYearTargetLaunchDate = formData.targetLaunchDate;
          }
        } else {
          currentYearTargetLaunchDate = formData.targetLaunchDate;
        }
      }
    } else {
      currentYearTargetLaunchDate = formData.targetLaunchDate;
    }

    // CRITICAL: For base year, exclude targetLaunchDate from currentPackData spread
    // For base year, targetLaunchDate is global and must come from formData, not currentPackData
    // currentPackData might have wrong targetLaunchDate (from previous calculations)
    let currentPackDataToSpread = currentPackData;
    if (
      currentTemplateType === 'nc' &&
      (activeYearTab === 0 || activeYearTab === null) &&
      currentPackData &&
      Object.keys(currentPackData).length > 0
    ) {
      // For base year, exclude targetLaunchDate from currentPackData
      const { targetLaunchDate: _targetLaunchDateBase, ...rest } = currentPackData;
      currentPackDataToSpread = rest;
    }

    const updatedData = {
      ...formData,
      // Spread currentPackData (without targetLaunchDate for base year)
      ...currentPackDataToSpread,
      [fieldName]: value,
      // CRITICAL: Always include supplierDiscount and supplierCode (even if 0 or undefined)
      // This ensures discount is available for gsmsNetCostFromMfg calculation
      supplierDiscount:
        supplierDiscountFromPack !== undefined
          ? supplierDiscountFromPack
          : formData.supplierDiscount !== undefined
            ? formData.supplierDiscount
            : 0,
      supplierCode:
        supplierCodeFromPack !== undefined
          ? supplierCodeFromPack
          : formData.supplierCode !== undefined
            ? formData.supplierCode
            : '',
      // For NC templates, always include gsmsPackSize from the pack size tab (readonly field)
      ...(currentTemplateType === 'nc' &&
        packSizeFromTab !== null && { gsmsPackSize: String(packSizeFromTab) }),
      // CRITICAL: When fieldName is gsmsHndlgType, use the NEW value (not state - state may be one step behind)
      // Otherwise use component state (Project Information) for other fields
      // So recalculateFields can compute GSMS Bottle Capacity (needs handlingType + packSize)
      gsmsHndlgType:
        fieldName === 'gsmsHndlgType' && value != null && value !== ''
          ? String(value)
          : gsmsHndlgType || currentPackDataToSpread.gsmsHndlgType || formData.gsmsHndlgType || '',
      // CRITICAL: For NC templates, ensure we use the current year's targetLaunchDate
      // For base year: use formData.targetLaunchDate (global) - explicitly set it to override any wrong value
      // For option years: use year-specific date
      // This ensures recalculateFields gets the correct date for blendedWhlsFee calculation
      ...(currentTemplateType === 'nc' &&
        currentYearTargetLaunchDate && { targetLaunchDate: currentYearTargetLaunchDate }),
    };

    // When gsmsCostFromMfg was blurred without changing its value, signal recalcCore to skip
    // the forced blendedWhlsFee recalculation on NC option years.  The correct per-year fee
    // was already written by copyForAllYears and must not be overwritten by a no-op blur.
    if (isGsmsCostNoChangeBlur) {
      updatedData._skipBlendedWhlsFeeRecalc = true;
    }

    // Check if gsmsProfit was manually set (either via flag or by checking if user typed a value)
    // Check both currentPackData and formData to catch cases where value was just typed
    const gsmsProfitValue = currentPackData.gsmsProfit ?? formData.gsmsProfit;
    const gsmsProfitManuallySetFlag =
      currentPackData._gsmsProfitManuallySet ?? formData._gsmsProfitManuallySet;
    const isGsmsProfitManuallySet =
      gsmsProfitManuallySetFlag === true ||
      wasGsmsProfitManuallySet ||
      (gsmsProfitValue !== null &&
        gsmsProfitValue !== undefined &&
        gsmsProfitValue !== '' &&
        gsmsProfitValue !== 0 &&
        gsmsProfitValue !== '0' &&
        gsmsProfitValue !== '0.00' &&
        parseFloat(gsmsProfitValue) !== 0);

    // For GSMS Profit, ensure the manually set flag is preserved in updatedData
    // This is critical to prevent circular recalculations when goal seek updates other fields
    if (fieldName === 'gsmsProfit') {
      // If gsmsProfit is being manually changed, set the flag
      if (value !== null && value !== undefined) {
        updatedData._gsmsProfitManuallySet = true;
        // Ensure the manually set value is preserved in updatedData
        // Preserve the exact typed value - don't format it
        // Formatting will happen in display components, but storage should preserve exact value
        updatedData.gsmsProfit = value;
      }
    } else if (fieldName === 'suggestedBidPrice') {
      // When suggestedBidPrice changes, check if gsmsProfit was manually set
      // If it was manually set, preserve it; otherwise recalculate
      if (isGsmsProfitManuallySet) {
        // Preserve manually set gsmsProfit value
        updatedData._gsmsProfitManuallySet = true;
        // Use the value from currentPackData first, then formData, then gsmsProfitValue
        const valueToPreserve =
          currentPackData.gsmsProfit ?? formData.gsmsProfit ?? gsmsProfitValue;
        if (
          valueToPreserve !== null &&
          valueToPreserve !== undefined &&
          valueToPreserve !== '' &&
          valueToPreserve !== 0 &&
          valueToPreserve !== '0' &&
          valueToPreserve !== '0.00'
        ) {
          updatedData.gsmsProfit = valueToPreserve;
        }
      } else {
        // When suggestedBidPrice changes and gsmsProfit was NOT manually set, clear the manual flag so GSMS Profit will be recalculated
        // This ensures GSMS Profit% updates when Suggested Bid Price changes
        updatedData._gsmsProfitManuallySet = false;
        // Don't preserve gsmsProfit value - remove it so it recalculates based on new suggested bid price
        delete updatedData.gsmsProfit;
      }
    } else if (fieldName === 'gsmsCostFromMfg' || fieldName === 'gsmsNetCostFromMfg') {
      // CRITICAL: When gsmsCostFromMfg or gsmsNetCostFromMfg changes, ALWAYS recalculate gsmsProfit%
      // This ensures two-way binding: changing cost from mfg → updates profit%
      // Even if gsmsProfit was manually set before, it should be recalculated based on the new cost
      updatedData._gsmsProfitManuallySet = false;
      // Don't preserve gsmsProfit value - let it recalculate
      // CRITICAL: Explicitly remove gsmsProfit to ensure it gets recalculated with the new cost
      delete updatedData.gsmsProfit;
      // Also ensure that when gsmsCost changes (which will happen after this recalculation),
      // gsmsProfit will be recalculated by marking it for recalculation
      updatedData._forceRecalculateGsmsProfit = true;
    } else if (fieldName === 'gsmsCost') {
      // When GSMS Cost changes (e.g., from recalculation), automatically recalculate GSMS Profit
      // unless it was manually set
      if (!isGsmsProfitManuallySet) {
        // Clear the manual flag so that GSMS Profit will be recalculated based on the new cost
        updatedData._gsmsProfitManuallySet = false;
        // Don't preserve gsmsProfit value - let it recalculate
        delete updatedData.gsmsProfit;
      } else {
        // Preserve manually set gsmsProfit value
        updatedData._gsmsProfitManuallySet = true;
        if (
          currentPackData.gsmsProfit !== null &&
          currentPackData.gsmsProfit !== undefined &&
          currentPackData.gsmsProfit !== ''
        ) {
          updatedData.gsmsProfit = currentPackData.gsmsProfit;
        } else if (
          formData.gsmsProfit !== null &&
          formData.gsmsProfit !== undefined &&
          formData.gsmsProfit !== ''
        ) {
          updatedData.gsmsProfit = formData.gsmsProfit;
        }
      }
    } else if (wasGsmsProfitManuallySet && fieldName !== 'suggestedBidPrice') {
      // If gsmsProfit was previously manually set and we're changing a different field,
      // preserve the flag so gsmsProfit doesn't get recalculated
      // This preserves the goal seek result when other fields change
      // IMPORTANT: Don't preserve if suggestedBidPrice is changing (checked above)
      updatedData._gsmsProfitManuallySet = true;
      // Also preserve the current gsmsProfit value
      if (
        currentPackData.gsmsProfit !== null &&
        currentPackData.gsmsProfit !== undefined &&
        currentPackData.gsmsProfit !== ''
      ) {
        updatedData.gsmsProfit = currentPackData.gsmsProfit;
      } else if (
        formData.gsmsProfit !== null &&
        formData.gsmsProfit !== undefined &&
        formData.gsmsProfit !== ''
      ) {
        updatedData.gsmsProfit = formData.gsmsProfit;
      }
    }

    // For NC, use 'indirect' as productType since NC formulas are similar to Indirect
    const productTypeForRecalc = templateType === 'nc' ? 'indirect' : productType;
    const yearIndexForBlur =
      templateType === 'nc' && activeYearTab !== null && activeYearTab !== 5
        ? activeYearTab
        : undefined;

    const recalculatedData = recalculateFields({
      updatedData,
      changedField: fieldName,
      productType: productTypeForRecalc,
      templateData,
      analysisType,
      contractPriceRanking: contractPriceRanking as Array<{ price_rank: number; price: number }>,
      templateType: templateType,
      supplierName: supplierName,
      yearIndex: yearIndexForBlur,
    });

    // Ensure _gsmsProfitManuallySet flag is preserved in recalculated data
    // This prevents circular recalculations when goal seek updates other fields
    if (fieldName === 'gsmsProfit' && wasGsmsProfitManuallySet) {
      recalculatedData._gsmsProfitManuallySet = true;
    } else if (recalculatedData._gsmsProfitManuallySet === true) {
      // Preserve the flag if it was set during recalculation
      // This ensures that when other fields change (like gsmsCost after goal seek),
      // the flag is still present to prevent gsmsProfit from being recalculated
    }

    // CRITICAL: If suggestedBidPrice is blurring and gsmsProfit was manually set, ensure it's preserved
    // This is a final safeguard to prevent gsmsProfit from being reset to 0
    if (fieldName === 'suggestedBidPrice' && isGsmsProfitManuallySet) {
      // Preserve the manually set gsmsProfit value from updatedData (which was set above)
      if (
        updatedData.gsmsProfit !== null &&
        updatedData.gsmsProfit !== undefined &&
        updatedData.gsmsProfit !== ''
      ) {
        // Preserve the exact typed value - don't format it
        recalculatedData.gsmsProfit = updatedData.gsmsProfit;
        recalculatedData._gsmsProfitManuallySet = true;
      }
    }

    // Update pack size/year specific data
    // For NC, update yearFormData; for MAS, update packSizeFormData
    const currentTemplateTypeAfterRecalc = bidDetails?.template_type || 'mas';

    // CRITICAL: If gsmsCost changed during recalculation (not as the changedField),
    // ensure gsmsProfit is recalculated unless it was manually set
    // This handles the case where gsmsCost changes due to other field changes (e.g., gsmsCostFromMfg)
    // NOTE: This check happens AFTER recalculateFields, so gsmsProfit may have already been recalculated
    // We should NOT delete it again if it was already recalculated
    if (fieldName !== 'gsmsCost' && fieldName !== 'gsmsProfit') {
      const oldGsmsCost = currentPackData.gsmsCost ?? formData.gsmsCost;
      const newGsmsCost = recalculatedData.gsmsCost;
      if (
        oldGsmsCost !== null &&
        oldGsmsCost !== undefined &&
        newGsmsCost !== null &&
        newGsmsCost !== undefined
      ) {
        const oldCostNum = parseFloat(String(oldGsmsCost));
        const newCostNum = parseFloat(String(newGsmsCost));
        const gsmsCostChanged =
          !isNaN(oldCostNum) && !isNaN(newCostNum) && Math.abs(oldCostNum - newCostNum) > 0.001;

        // CRITICAL: When gsmsCost changes, gsmsProfit should already be recalculated by recalculateFields
        // We should NOT delete it here - that would clear the recalculated value
        // Ensure the manual flag is cleared since it was recalculated
        if (gsmsCostChanged) {
          const isCostFromMfgChange =
            fieldName === 'gsmsCostFromMfg' || fieldName === 'gsmsNetCostFromMfg';
          const gsmsProfitAlreadyRecalculated =
            recalculatedData.gsmsProfit !== undefined &&
            recalculatedData.gsmsProfit !== null &&
            recalculatedData.gsmsProfit !== '';

          if (gsmsProfitAlreadyRecalculated) {
            // Ensure the manual flag is cleared since it was recalculated
            recalculatedData._gsmsProfitManuallySet = false;
          } else if (isCostFromMfgChange) {
            // If gsmsProfit wasn't recalculated and this was triggered by cost from mfg change,
            // ensure it gets recalculated (shouldn't happen, but safety check)
            recalculatedData._gsmsProfitManuallySet = false;
            recalculatedData._forceRecalculateGsmsProfit = true;
          }
        }
      }
    }

    // CRITICAL: Preserve the exact typed value for the field that was changed
    // This ensures that when switching tabs, the exact value the user typed is displayed
    // Don't let recalculated/formatted values overwrite the user's typed input
    // ALWAYS preserve the typed value, even if recalculation set a formatted value
    // This must happen AFTER recalculation to override any formatted values
    if (value !== null && value !== undefined) {
      // Preserve the exact typed value - don't use formatted/recalculated value
      // This ensures tab switching shows the exact value the user typed
      // Store as-is: if it's a number, keep it as number; if it's a string, keep it as string
      recalculatedData[fieldName] = value;
    }

    // Update global form data - preserve global fields that shouldn't be overwritten
    // This will also trigger autosave since formDataWithoutContracts is watched by useAutoSave hook
    // CRITICAL: For NC option years, targetLaunchDate is year-specific, not global
    // So we need to exclude it from preserveGlobalFields for option years
    const isNCOptionYear =
      currentTemplateTypeAfterRecalc === 'nc' &&
      activePackSizeTab !== null &&
      activeYearTab !== null &&
      activeYearTab > 0 &&
      activeYearTab !== 5;

    // For NC option years, preserve global fields but exclude targetLaunchDate (it's year-specific)
    // For base year and MAS, use default preserveGlobalFields behavior
    let preservedFormData: any;
    if (isNCOptionYear) {
      // For option years, preserve global fields except targetLaunchDate
      // Use preserveGlobalFieldsLogic directly with custom globalFields list
      const globalFieldsWithoutTargetLaunchDate = [
        'contracts',
        'contractsData',
        'supplierName',
        'analysisDate',
        'analysisType',
        'productName',
        'analystName',
        'gsmsHndlgType',
      ];
      preservedFormData = preserveGlobalFieldsLogic(
        recalculatedData,
        formData,
        globalFieldsWithoutTargetLaunchDate
      );
    } else {
      // For base year and MAS, use default preserveGlobalFields
      preservedFormData = preserveGlobalFields(recalculatedData);
    }

    preservedFormData = {
      ...preservedFormData,
      // CRITICAL: Ensure the exact typed value is preserved in formData too
      [fieldName]: value !== null && value !== undefined ? value : recalculatedData[fieldName],
      // CRITICAL: Ensure contractAnnualGP is preserved if it exists and is not 0
      ...(recalculatedData.contractAnnualGP &&
      recalculatedData.contractAnnualGP !== '0' &&
      recalculatedData.contractAnnualGP !== '0.00' &&
      parseFloat(recalculatedData.contractAnnualGP) !== 0
        ? { contractAnnualGP: recalculatedData.contractAnnualGP }
        : {}),
      // CRITICAL: Always use blendedWhlsFee from recalculatedData (newly calculated)
      // Don't let preserveGlobalFields overwrite it with old formData value
      // blendedWhlsFee is NOT a global field, so it should come from recalculatedData
      ...(recalculatedData.blendedWhlsFee && {
        blendedWhlsFee: recalculatedData.blendedWhlsFee,
      }),
      // CRITICAL: Always use gsmsProfit from recalculatedData if it was recalculated
      // Don't let preserveGlobalFields overwrite it with old formData value
      // This ensures that when gsmsCostFromMfg changes, the recalculated gsmsProfit is preserved
      ...(recalculatedData.gsmsProfit !== undefined &&
        recalculatedData.gsmsProfit !== null &&
        recalculatedData.gsmsProfit !== '' &&
        fieldName !== 'gsmsProfit' && {
          gsmsProfit: recalculatedData.gsmsProfit,
        }),
    };

    if (currentTemplateTypeAfterRecalc === 'nc' && activePackSizeTab !== null) {
      // CRITICAL: For base year (activeYearTab === 0), targetLaunchDate is global and comes from formData
      // For option years (activeYearTab > 0), targetLaunchDate is year-specific
      let updatedYearData: any;

      if (activeYearTab === 0 || activeYearTab === null) {
        // Base year: targetLaunchDate is global, use from preservedFormData (from formData)
        // CRITICAL: recalculatedData.targetLaunchDate might be wrong if it came from currentPackData
        // Always use preservedFormData.targetLaunchDate for base year
        updatedYearData = {
          ...recalculatedData, // This now has the exact typed value preserved above
          // CRITICAL: For base year, always use targetLaunchDate from preservedFormData (global from formData)
          // Don't use recalculatedData.targetLaunchDate which might be wrong
          targetLaunchDate: preservedFormData.targetLaunchDate,
          // CRITICAL: Also use blendedWhlsFee from preservedFormData (calculated with correct date)
          blendedWhlsFee: preservedFormData.blendedWhlsFee,
          // CRITICAL: Ensure gsmsProfit from recalculatedData is preserved (if it was recalculated)
          ...(recalculatedData.gsmsProfit !== undefined &&
            recalculatedData.gsmsProfit !== null &&
            recalculatedData.gsmsProfit !== '' && {
              gsmsProfit: recalculatedData.gsmsProfit,
            }),
        };
      } else {
        // Option years: preserve year-specific targetLaunchDate
        // Priority: 1) updatedData.targetLaunchDate (from currentYearTargetLaunchDate we set),
        //           2) currentPackData.targetLaunchDate (from yearFormData),
        //           3) recalculatedData.targetLaunchDate (from recalculateFields, might be base year's)
        let yearSpecificTargetLaunchDate =
          updatedData.targetLaunchDate || currentPackData.targetLaunchDate;

        // If we still don't have a year-specific date and we're in an option year, calculate it
        if (!yearSpecificTargetLaunchDate && activeYearTab > 0 && activeYearTab !== 5) {
          const baseDate = formData.targetLaunchDate || recalculatedData.targetLaunchDate;
          if (baseDate) {
            try {
              const base = typeof baseDate === 'string' ? new Date(baseDate) : baseDate;
              if (base instanceof Date && !isNaN(base.getTime())) {
                const yearDate = new Date(base);
                yearDate.setFullYear(base.getFullYear() + activeYearTab);
                yearSpecificTargetLaunchDate = yearDate;
              }
            } catch (_e) {
              // If calculation fails, use base date
              yearSpecificTargetLaunchDate = baseDate;
            }
          }
        }

        updatedYearData = {
          ...recalculatedData, // This now has the exact typed value preserved above
          // CRITICAL: Always preserve year-specific targetLaunchDate for option years
          // This ensures blendedWhlsFee is calculated with the correct year-specific date
          ...(yearSpecificTargetLaunchDate && {
            targetLaunchDate: yearSpecificTargetLaunchDate,
          }),
          // CRITICAL: Explicitly set blendedWhlsFee from preservedFormData (which has the correct value from recalculatedData)
          // This ensures option years store their calculated blendedWhlsFee in yearFormData
          ...(preservedFormData.blendedWhlsFee && {
            blendedWhlsFee: preservedFormData.blendedWhlsFee,
          }),
          // CRITICAL: Ensure gsmsProfit from recalculatedData is preserved (if it was recalculated)
          ...(recalculatedData.gsmsProfit !== undefined &&
            recalculatedData.gsmsProfit !== null &&
            recalculatedData.gsmsProfit !== '' && {
              gsmsProfit: recalculatedData.gsmsProfit,
            }),
        };
      }

      // CRITICAL: Update ref BEFORE setState so the next render (which reads yearFormDataRef for currentYearData) sees the new gsmsProfit immediately
      const updatedYearFormData = {
        ...yearFormDataRef.current,
        [activePackSizeTab as number]: {
          ...(yearFormDataRef.current[activePackSizeTab as number] || {}),
          [activeYearTab as number]: updatedYearData,
        },
      };
      yearFormDataRef.current = updatedYearFormData;

      setYearFormData(prev => ({
        ...prev,
        [activePackSizeTab as number]: {
          ...prev[activePackSizeTab as number],
          [activeYearTab as number]: updatedYearData,
        },
      }));
      // CRITICAL: For NC, immediately sync formData with the updated yearFormData
      // This ensures the form displays the correct value
      // preservedFormData already has blendedWhlsFee and gsmsProfit from recalculatedData (we set it above)
      // CRITICAL: Ensure gsmsProfit is in preservedFormData for NC templates
      // Get it from updatedYearData (which has it from recalculatedData) or recalculatedData directly
      const finalFormData = {
        ...preservedFormData,
        // Ensure gsmsProfit is included from recalculatedData or updatedYearData
        ...(recalculatedData.gsmsProfit !== undefined &&
          recalculatedData.gsmsProfit !== null &&
          recalculatedData.gsmsProfit !== '' && {
            gsmsProfit: recalculatedData.gsmsProfit,
          }),
        // CRITICAL: For NC option years, recalculatedData.targetLaunchDate is the
        // option-year-specific date (e.g. base+3 years). We must NOT let it overwrite
        // formData.targetLaunchDate which is the global base-year date used to display
        // the Target Launch Date field and derive all other year offsets.
        // Always restore the base-year date into formData when in an option year.
        ...(isNCOptionYear &&
          formData.targetLaunchDate !== undefined && {
            targetLaunchDate: formData.targetLaunchDate,
          }),
      };

      setFormData(finalFormData);
    } else {
      // This will trigger autosave since packSizeFormData is watched by useAutoSave hook
      setPackSizeFormData(prev => ({
        ...prev,
        [activePackSizeTab as number]: recalculatedData, // This now has the exact typed value preserved above
      }));
      setFormData(preservedFormData);
    }

    // Mark that user has made changes - enable auto-save after initial load
    hasUserMadeChangesRef.current = true;

    // Update chart date range and reload chart data when usage period dates change
    // Use the event value directly (for calendar picker) or the updated value from formData
    if (fieldName === 'usagePeriodStartDate' || fieldName === 'usagePeriodEndDate') {
      // Get the current pack data after update
      // For NC, use yearFormData; for MAS, use packSizeFormData
      const currentTemplateTypeForDates = bidDetails?.template_type || 'mas';
      const currentPackData =
        currentTemplateTypeForDates === 'nc' && activePackSizeTab !== null
          ? yearFormData[activePackSizeTab as number]?.[activeYearTab as number] || formData
          : packSizeFormData[activePackSizeTab as number] || formData;

      // Use the event value if it's a Date object (from calendar), otherwise use the updated value
      const updatedStartDate =
        fieldName === 'usagePeriodStartDate'
          ? event instanceof Date
            ? event
            : recalculatedData.usagePeriodStartDate || currentPackData.usagePeriodStartDate
          : currentPackData.usagePeriodStartDate || formData.usagePeriodStartDate;

      const updatedEndDate =
        fieldName === 'usagePeriodEndDate'
          ? event instanceof Date
            ? event
            : recalculatedData.usagePeriodEndDate || currentPackData.usagePeriodEndDate
          : currentPackData.usagePeriodEndDate || formData.usagePeriodEndDate;

      // Update chart date range if both dates are available
      if (updatedStartDate && updatedEndDate) {
        try {
          // Parse dates - handle both Date objects and strings
          const parsedStart =
            updatedStartDate instanceof Date ? updatedStartDate : parseDateString(updatedStartDate);
          const parsedEnd =
            updatedEndDate instanceof Date ? updatedEndDate : parseDateString(updatedEndDate);

          if (
            parsedStart &&
            parsedEnd &&
            !isNaN(parsedStart.getTime()) &&
            !isNaN(parsedEnd.getTime())
          ) {
            // Update chart date range immediately
            setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
            setCalendarViewDates({ startViewDate: parsedStart, endViewDate: parsedEnd });

            // Reload chart data with new dates
            const conceptName =
              recalculatedData.conceptName || currentPackData.conceptName || formData.conceptName;
            const packSize =
              recalculatedData.packSize || currentPackData.packSize || formData.packSize;

            if (conceptName && packSize && bidId) {
              // Ensure dates are Date objects for API calls
              const parsedStartDate =
                parsedStart instanceof Date ? parsedStart : new Date(parsedStart);
              const parsedEndDate = parsedEnd instanceof Date ? parsedEnd : new Date(parsedEnd);

              // Format dates as strings for contract price ranking API
              const _startDateStr3 = formatDateForAPI(updatedStartDate);
              const _endDateStr3 = formatDateForAPI(updatedEndDate);

              // Force reload by passing true for skipStore check
              loadMarketSalesTrendData(null, true, parsedStartDate, parsedEndDate);
              loadMarketShareByVolume(true, parsedStartDate, parsedEndDate);
              // Format dates to yyyy-mm-dd for contract price ranking API
              const contractRankingStartDate = formatDateForChartAPI(updatedStartDate);
              const contractRankingEndDate = formatDateForChartAPI(updatedEndDate);
              if (contractRankingStartDate && contractRankingEndDate) {
                loadContractPriceRanking(contractRankingStartDate, contractRankingEndDate, true);
              }
            }
          }
        } catch (error) {
          console.error('Error updating chart date range from usage period:', error);
        }
      }
    }
  };

  const replicateAcrossPackSizes = (fieldName: string, value: any) => {
    // Special handling for gsms_profit: check if pack sizes are validated (isolate on concept level)
    if (fieldName === 'gsmsProfit') {
      const currentData = packSizeFormData[activePackSizeTab as number] || formData;
      const currentConcept = currentData.conceptName || selectedConcept;

      if (!currentConcept) {
        toast.error('Cannot replicate GSMS Profit: No concept selected', { autoClose: 5000 });
        return;
      }

      // Check if suggested bid price exists (from source pack size)
      const sourcePackSuggestedBidPrice = parseFloat(currentData.suggestedBidPrice || '0');
      if (sourcePackSuggestedBidPrice <= 0) {
        toast.error(
          'Cannot replicate GSMS Profit: Suggested BID Price is required. Please enter a Suggested BID Price first.',
          { autoClose: 5000 }
        );
        return;
      }

      const invalidPackSizes: string[] = [];
      const validPackSizes: number[] = [];

      // Check validation status for pack sizes in the CURRENT CONCEPT only
      const existingPackSizeTabs = Object.keys(packSizeFormData).map(key => parseInt(key));
      existingPackSizeTabs.forEach(tabIndex => {
        const tabData = packSizeFormData[tabIndex];
        // Only check pack sizes that belong to the current concept
        if (tabData && tabData.conceptName === currentConcept) {
          if (!tabData.ndcValidated) {
            // Get pack size for error message
            const packSize = tabData.packSize || 'Unknown';
            invalidPackSizes.push(`Pack Size ${packSize}`);
          } else {
            validPackSizes.push(tabIndex);
          }
        }
      });

      // If any pack sizes in this concept are not validated, show toast and don't replicate
      if (invalidPackSizes.length > 0) {
        toast.error(
          `Cannot replicate GSMS Profit for ${currentConcept}: The following pack sizes are not validated: ${invalidPackSizes.join(', ')}`,
          { autoClose: 5000 }
        );
        return;
      }

      // If no valid pack sizes in this concept, show error
      if (validPackSizes.length === 0) {
        toast.error(
          `Cannot replicate GSMS Profit for ${currentConcept}: No validated pack sizes found`,
          { autoClose: 5000 }
        );
        return;
      }

      // Check if all target pack sizes have suggested bid price
      const packSizesWithoutSuggestedBidPrice: string[] = [];
      validPackSizes.forEach(tabIndex => {
        const tabData = packSizeFormData[tabIndex] || {};
        const targetSuggestedBidPrice = parseFloat(tabData.suggestedBidPrice || '0');
        if (targetSuggestedBidPrice <= 0) {
          const packSize = tabData.packSize || 'Unknown';
          packSizesWithoutSuggestedBidPrice.push(`Pack Size ${packSize}`);
        }
      });

      // If any target pack sizes don't have suggested bid price, show error
      if (packSizesWithoutSuggestedBidPrice.length > 0) {
        toast.error(
          `Cannot replicate GSMS Profit: The following pack sizes need Suggested BID Price: ${packSizesWithoutSuggestedBidPrice.join(', ')}. Please enter Suggested BID Price for these pack sizes first.`,
          { autoClose: 5000 }
        );
        return;
      }

      // Only replicate to validated pack sizes in the current concept
      // IMPORTANT: For GSMS Profit replication, trigger goal seek if suggested bid price exists
      // Goal seek will work backwards: GSMS Profit → Target GSMS Cost → Target Net Cost From Mfg → Target Cost From Mfg
      const updatedPackSizes: Record<number, any> = {};

      // Get the source pack size data (the one we're replicating FROM) to use its suggestedBidPrice for goal seek
      const sourcePackSizeData = packSizeFormData[activePackSizeTab as number] || formData;
      const sourceSuggestedBidPrice = parseFloat(sourcePackSizeData.suggestedBidPrice || '0');

      validPackSizes.forEach(tabIndex => {
        const currentTabData = packSizeFormData[tabIndex] || {};

        // IMPORTANT: Preserve ALL existing data from the pack size, including:
        // - suggestedBidPrice (needed for goal seek calculation) - CRITICAL for goal seek to work
        //   All target pack sizes should have suggestedBidPrice (validated above)
        // - packSize, conceptName (needed for calculations)
        // - All COGS components (bottle cost, cap cost, etc. - needed for Direct product goal seek)
        // - All other fields
        const targetSuggestedBidPrice = parseFloat(currentTabData.suggestedBidPrice || '0');
        // Use target's suggestedBidPrice (should always be > 0 after validation above)
        const suggestedBidPriceForGoalSeek =
          targetSuggestedBidPrice > 0 ? targetSuggestedBidPrice : sourceSuggestedBidPrice;

        const updatedData = {
          ...currentTabData,
          // Ensure gsmsProfit is set as a string (consistent with form data format)
          [fieldName]:
            typeof value === 'string'
              ? value
              : value !== null && value !== undefined
                ? value.toString()
                : value,
          // Set suggestedBidPrice for goal seek calculation (use target's if available, otherwise source's)
          // This is CRITICAL for goal seek to work - it requires suggestedBidPrice > 0
          suggestedBidPrice:
            suggestedBidPriceForGoalSeek > 0
              ? suggestedBidPriceForGoalSeek.toString()
              : currentTabData.suggestedBidPrice || '', // Keep original if no source price
        };

        // Trigger recalculation for this pack size (this will trigger goal seek if suggested bid price exists)
        // When changedField === 'gsmsProfit', recalculateFields will:
        // 1. If suggestedBidPrice > 0: Trigger goal seek to update independent variables (Cost From Mfg, Net Cost From Mfg, GSMS Cost)
        // 2. If suggestedBidPrice is not set: Just store the value (goal seek will trigger when suggested bid price is set later)
        // Goal seek updates: gsmsCostFromMfg → triggers recalculation of gsmsNetCostFromMfg → triggers recalculation of gsmsCost
        // The local recalculateFields wrapper will pass productType, templateData, and analysisType to recalculateFields
        const recalculatedData = recalculateFields(updatedData, fieldName);

        // Ensure the replicated GSMS Profit value is preserved
        // Goal seek will have updated the independent variables (gsmsCostFromMfg, gsmsNetCostFromMfg, gsmsCost)
        // to achieve the target profit, and the recalculated GSMS Profit should match
        recalculatedData[fieldName] = value;

        // Mark that GSMS Profit was manually set (for this pack size)
        // This ensures that when suggested bid price is set later, goal seek will trigger instead of recalculating profit
        recalculatedData._gsmsProfitManuallySet = true;

        // IMPORTANT: Restore the target pack size's original suggestedBidPrice if it didn't have one
        // We only used source's suggestedBidPrice for goal seek calculation, but don't overwrite target's field
        if (targetSuggestedBidPrice === 0 && sourcePackSuggestedBidPrice > 0) {
          // Target didn't have suggestedBidPrice, so don't save source's value to target
          // Keep the original (empty/null) value
          recalculatedData.suggestedBidPrice = currentTabData.suggestedBidPrice || '';
        } else if (targetSuggestedBidPrice > 0) {
          // Target has its own suggestedBidPrice, keep it
          recalculatedData.suggestedBidPrice = currentTabData.suggestedBidPrice;
        }

        // Merge with existing pack size data to ensure all fields are preserved
        // IMPORTANT: recalculatedData contains all updated fields from goal seek (gsmsCostFromMfg, gsmsNetCostFromMfg, gsmsCost, etc.)
        // We use recalculatedData as the base since it contains all calculated values, then only preserve non-calculated fields from currentTabData
        // This ensures goal seek updates are properly applied and not overwritten by old values
        updatedPackSizes[tabIndex] = {
          ...currentTabData, // First preserve all existing data (for fields like conceptName, packSize, ndcValidated, etc. that aren't recalculated)
          ...recalculatedData, // Then override with all recalculated values (includes goal seek updates: gsmsCostFromMfg, gsmsNetCostFromMfg, gsmsCost, gsmsIffFee, etc.)
          [fieldName]: value, // Finally ensure GSMS Profit is exactly the replicated value
        };
      });

      // Update all pack size data at once
      setPackSizeFormData(prev => ({
        ...prev,
        ...updatedPackSizes,
      }));

      // If the current active tab is one of the updated tabs, also update formData
      if (updatedPackSizes[activePackSizeTab as number]) {
        // Preserve global fields when updating formData
        setFormData(preserveGlobalFields(updatedPackSizes[activePackSizeTab as number]));
      }

      // Show success toast - ensure it stays for 5 seconds (override container default of 3000ms)
      toast.success(
        `GSMS Profit replicated across ${validPackSizes.length} validated pack size(s) for ${currentConcept}`,
        {
          autoClose: 5000,
          closeOnClick: false,
        }
      );
      return;
    }

    // For other fields, replicate to validated pack sizes in the current concept (same logic as GSMS Profit)
    const currentData = packSizeFormData[activePackSizeTab as number] || formData;
    const currentConcept = currentData.conceptName || selectedConcept;

    if (!currentConcept) {
      toast.error(`Cannot replicate ${fieldName}: No concept selected`, { autoClose: 5000 });
      return;
    }

    // Field name to display name mapping for toast messages
    const fieldDisplayNames: Record<string, string> = {
      vaTmsPercent: 'VA TMS %',
      dodTmsPercent: 'DOD TMS %',
      totalTmsPercent: 'Total TMS %',
      suggestedBidPrice: 'Suggested BID Price',
      wac: 'WAC',
      gsmsProfit: 'GSMS Profit',
    };

    const fieldDisplayName = fieldDisplayNames[fieldName] || fieldName;

    const invalidPackSizes: string[] = [];
    const validPackSizes: number[] = [];

    // Check validation status for pack sizes in the CURRENT CONCEPT only
    const existingPackSizeTabs = Object.keys(packSizeFormData).map(key => parseInt(key));
    existingPackSizeTabs.forEach(tabIndex => {
      const tabData = packSizeFormData[tabIndex];
      // Only check pack sizes that belong to the current concept
      if (tabData && tabData.conceptName === currentConcept) {
        if (!tabData.ndcValidated) {
          // Get pack size for error message
          const packSize = tabData.packSize || 'Unknown';
          invalidPackSizes.push(`Pack Size ${packSize}`);
        } else {
          validPackSizes.push(tabIndex);
        }
      }
    });

    // If any pack sizes in this concept are not validated, show toast and don't replicate
    if (invalidPackSizes.length > 0) {
      toast.error(
        `Cannot replicate ${fieldDisplayName} for ${currentConcept}: The following pack sizes are not validated: ${invalidPackSizes.join(', ')}`,
        { autoClose: 5000 }
      );
      return;
    }

    // If no valid pack sizes in this concept, show error
    if (validPackSizes.length === 0) {
      toast.error(
        `Cannot replicate ${fieldDisplayName} for ${currentConcept}: No validated pack sizes found`,
        { autoClose: 5000 }
      );
      return;
    }

    // Only replicate to validated pack sizes in the current concept
    const updatedPackSizes: Record<number, any> = {};

    validPackSizes.forEach(tabIndex => {
      const currentTabData = packSizeFormData[tabIndex] || {};

      // Update the field value
      const updatedData = {
        ...currentTabData,
        [fieldName]: value,
      };

      // Trigger recalculation for this pack size (this will update all dependent fields)
      const recalculatedData = recalculateFields(updatedData, fieldName);

      updatedPackSizes[tabIndex] = recalculatedData;
    });

    // Update all pack size data at once
    setPackSizeFormData(prev => ({
      ...prev,
      ...updatedPackSizes,
    }));

    // If the current active tab is one of the updated tabs, also update formData
    if (updatedPackSizes[activePackSizeTab as number]) {
      // Preserve global fields when updating formData
      setFormData(preserveGlobalFields(updatedPackSizes[activePackSizeTab as number]));
    }

    // Show success toast for replication (same format as GSMS Profit) - ensure it stays for 5 seconds (override container default of 3000ms)
    toast.success(
      `${fieldDisplayName} replicated across ${validPackSizes.length} validated pack size(s) for ${currentConcept}`,
      {
        autoClose: 5000,
        closeOnClick: false,
      }
    );
  };

  // Ref to track if tab change is in progress (prevents multiple rapid clicks)

  const handlePackSizeTabChange = async (tabIndex: number) => {
    // Prevent multiple rapid clicks that could cause both tabs to be highlighted
    if (tabIndex === activePackSizeTab || isTabChangingRef.current) {
      return; // Don't do anything if clicking the same tab or if change is in progress
    }

    // Mark that tab change is in progress
    isTabChangingRef.current = true;

    try {
      // CRITICAL: Save current tab data to packSizeFormData state and ref before switching
      // This ensures all current formData (including any unsaved changes) is preserved
      // We update both state and ref to ensure handleSaveBid has access to latest data
      setPackSizeFormData(prev => {
        const updated = {
          ...prev,
          [activePackSizeTab as number]: {
            ...formData, // Save current formData (including any unsaved global field changes)
          },
        };
        // Update ref immediately so handleSaveBid can access latest data
        packSizeFormDataRef.current = updated;
        return updated;
      });

      // For NC template, also save current year data before switching pack sizes
      const currentTemplateType = bidDetails?.template_type || 'mas';
      if (currentTemplateType === 'nc' && activePackSizeTab !== null) {
        setYearFormData(prev => {
          const updated = {
            ...prev,
            [activePackSizeTab as number]: {
              ...prev[activePackSizeTab as number],
              [activeYearTab as number]: {
                ...formData, // Save current formData as year data
              },
            },
          };
          yearFormDataRef.current = updated;
          return updated;
        });
      }

      // Update active tab immediately - don't block user interaction
      setActivePackSizeTab(tabIndex);

      // For NC template, reset to Base Year (yearIndex 0) when switching pack sizes
      if (currentTemplateType === 'nc') {
        setActiveYearTab(0);
      }

      // Trigger save in background (non-blocking) to ensure current tab's data is persisted
      // This ensures that if user updated fields in the current tab, they are saved even if
      // auto-save hasn't completed yet (auto-save has 3 second delay)
      // handleSaveBid uses packSizeFormDataRef to get the latest data, including the current tab's updates
      // Autosave disabled - user must click Save button manually
      // We don't await this - let it run in background so tab switch is instant
      // if (bidId && !isSaving && !isInitialLoadRef.current) {
      //   // Use setTimeout to ensure state update is processed, then save asynchronously
      //   setTimeout(async () => {
      //     try {
      //       await handleSaveBidRef.current(true);
      //     } catch (error) {
      //       // Silently handle save errors - don't interrupt user
      //       console.error('Error saving during tab switch:', error);
      //     }
      //   }, 50); // Small delay to ensure state is updated
      // }

      // Load data for the new tab from ref (same prev we just saved to) so typed values are retained
      let newTabData = packSizeFormDataRef.current[tabIndex] ?? packSizeFormData[tabIndex] ?? {};

      // Recalculate gsmsNetCostFromMfg only when the tab has no value (don't overwrite user-typed values)
      const gsmsCostFromMfg = parseFloat(newTabData.gsmsCostFromMfg) || 0;
      const currentGsmsNetCostFromMfg = parseFloat(newTabData.gsmsNetCostFromMfg) || 0;
      const hasUserValue =
        currentGsmsNetCostFromMfg > 0 ||
        (newTabData.gsmsNetCostFromMfg !== undefined &&
          newTabData.gsmsNetCostFromMfg !== '' &&
          newTabData.gsmsNetCostFromMfg !== null);
      const supplierDiscount = newTabData.supplierDiscount;

      if (
        !hasUserValue &&
        gsmsCostFromMfg > 0 &&
        supplierDiscount !== undefined &&
        supplierDiscount !== null &&
        supplierDiscount > 0
      ) {
        const supplierBulkNdc = newTabData.selectedMfgNdc || newTabData.mfg_ndc || '';
        const expectedNetCost = calculateGSMSNetCostFromMfg(
          gsmsCostFromMfg,
          supplierBulkNdc,
          supplierDiscount
        );
        newTabData = {
          ...newTabData,
          gsmsNetCostFromMfg: expectedNetCost.toFixed(2),
        };
      }

      // Preserve global fields when switching tabs
      // CRITICAL: Use functional update to get current formData state
      // This ensures we have the latest global fields (like contracts) even if user just updated them
      setFormData((currentFormData: any) => {
        return preserveGlobalFieldsLogic(newTabData, currentFormData);
      });

      // Load chart data from store first, then call APIs if not in store
      if (bidId && newTabData.conceptName && newTabData.packSize) {
        const _bidIdNumTab = parseInt(bidId);
        const concept = newTabData.conceptName;
        const packSize = parseFloat(newTabData.packSize);

        // Clear data on tab switch - will be loaded by useEffect when needed
        setMarketSalesTrendData([]);
        setFilteredMarketSalesTrendData([]);
        setMarketShareByVolume([]);
        setContractPriceRanking([]);

        // Load NDC options from store
        if (supplierName) {
          const storedNdcOptions = BidAnalysisStore.getNdcOptions(supplierName, concept, packSize);
          if (storedNdcOptions && storedNdcOptions.length > 0) {
            setNdcOptions(storedNdcOptions);
          } else {
            // If not in store, load from API (but don't force reload - just check store)
            loadNdcOptions(supplierName, concept, packSize, false);
          }
        }
      }
    } finally {
      // Reset the flag after tab change is complete
      isTabChangingRef.current = false;
    }
  };

  const handleYearTabChange = async (tabIndex: number) => {
    if (templateType !== 'nc') return;

    // Prevent multiple rapid clicks
    if (tabIndex === activeYearTab || isTabChangingRef.current || activePackSizeTab === null) {
      return;
    }

    isTabChangingRef.current = true;

    try {
      // Save current year tab data before switching - use nested structure
      // CRITICAL: Always use the ref's blendedWhlsFee for the year being saved (never formData's).
      // formData.blendedWhlsFee reflects the currently-displayed year, but if the ref has a
      // different (correct) value (e.g. from Copy), it must win so option-year fees are not
      // overwritten by the base-year fee that formData may still be showing.
      setYearFormData(prev => {
        const isValidFee = (v: any) =>
          v !== null &&
          v !== undefined &&
          v !== '' &&
          v !== '0.00' &&
          !isNaN(parseFloat(String(v)));

        // For the year being LEFT: use ref data as the source of truth, then fill missing keys
        // from formData. This ensures option years never inherit base-year-specific fields
        // (targetLaunchDate, blendedWhlsFee) from formData when the user leaves that year.
        const refYearData =
          yearFormDataRef.current[activePackSizeTab as number]?.[activeYearTab as number] || {};
        const stateYearData = prev[activePackSizeTab as number]?.[activeYearTab as number] || {};

        // Determine correct blendedWhlsFee to save: prefer ref > state > formData (only for base year)
        const refBlendedWhlsFee = refYearData?.blendedWhlsFee;
        const stateBlendedWhlsFee = stateYearData.blendedWhlsFee;
        const blendedWhlsFeeToSave = isValidFee(refBlendedWhlsFee)
          ? String(refBlendedWhlsFee)
          : isValidFee(stateBlendedWhlsFee)
            ? String(stateBlendedWhlsFee)
            : formData.blendedWhlsFee;

        // Build the data to save for the year being left.
        // For option years (activeYearTab > 0): the ref is the authority — only supplement with
        // formData for fields truly absent from the ref (user edits during this visit are already
        // in the ref via handleFieldChange). Never let formData's targetLaunchDate or blendedWhlsFee
        // overwrite the option-year-specific values stored in the ref.
        let yearDataToSave: any;
        if (activeYearTab !== null && activeYearTab > 0 && Object.keys(refYearData).length > 0) {
          // Option year: ref wins entirely; merge formData only for fields ref doesn't have
          const {
            targetLaunchDate: _ftld,
            blendedWhlsFee: _fbwf,
            ...formDataWithoutYearSpecific
          } = formData;
          yearDataToSave = {
            ...formDataWithoutYearSpecific,
            ...refYearData,
            ...(isValidFee(blendedWhlsFeeToSave) && { blendedWhlsFee: blendedWhlsFeeToSave }),
          };
        } else {
          // Base year (activeYearTab === 0) or no ref data: use formData as before
          yearDataToSave = {
            ...formData,
            ...(isValidFee(blendedWhlsFeeToSave) && { blendedWhlsFee: blendedWhlsFeeToSave }),
          };
        }

        // CRITICAL: Always prefer ref over state — ref is synchronously updated by copyForAllYears,
        // while React state may not have flushed yet. Using prev (state) as base after a Copy
        // would overwrite the ref with stale state, losing option year data for years 1-4.
        const base =
          yearFormDataRef.current && Object.keys(yearFormDataRef.current).length > 0
            ? yearFormDataRef.current
            : prev;

        const updated = {
          ...base,
          [activePackSizeTab as number]: {
            ...base[activePackSizeTab as number],
            [activeYearTab as number]: yearDataToSave,
          },
        };

        yearFormDataRef.current = updated;
        return updated;
      });

      // Update active year tab
      setActiveYearTab(tabIndex);

      // For NC templates, only recalculate blendedWhlsFee if it is genuinely missing from the
      // DESTINATION year. Check BOTH the main ref AND the copy-all-years ref before deciding
      // to recalculate — copy ref already has correct per-year fees computed during copyForAllYears.
      if (
        templateType === 'nc' &&
        tabIndex !== 5 &&
        tabIndex !== null &&
        activePackSizeTab !== null
      ) {
        const isValidFeeVal = (v: any) =>
          v !== null &&
          v !== undefined &&
          v !== '' &&
          v !== '0.00' &&
          v !== '0' &&
          !isNaN(parseFloat(String(v)));

        // Check main ref AND copy snapshot — either having the fee means we must NOT recalculate
        const destRefData = yearFormDataRef.current[activePackSizeTab as number]?.[tabIndex];
        const destCopyData = copyAllYearsDataRef.current?.[activePackSizeTab as number]?.[tabIndex];
        const destHasBlendedWhlsFee =
          isValidFeeVal(destRefData?.blendedWhlsFee) || isValidFeeVal(destCopyData?.blendedWhlsFee);

        if (!destHasBlendedWhlsFee) {
          // Only recalculate when blendedWhlsFee is truly missing (e.g. never visited tab and no copy)
          setTimeout(() => {
            setYearFormData(prev => {
              // Re-check both refs inside the updater (they may have been updated by now)
              const refData = yearFormDataRef.current[activePackSizeTab as number]?.[tabIndex];
              const copyData =
                copyAllYearsDataRef.current?.[activePackSizeTab as number]?.[tabIndex];
              if (
                isValidFeeVal(refData?.blendedWhlsFee) ||
                isValidFeeVal(copyData?.blendedWhlsFee)
              ) {
                return prev; // A valid fee is now present — skip recalculation entirely
              }
              // Use ref as primary source (most up-to-date), fall back to state only if ref empty
              const yearData =
                refData && Object.keys(refData).length > 0
                  ? refData
                  : prev[activePackSizeTab as number]?.[tabIndex];
              if (!yearData) return prev;
              const targetLaunchDate =
                yearData.targetLaunchDate || (templateData as any)?.targetLaunchDate;
              if (!targetLaunchDate) return prev;
              const recalculatedData = recalculateFields({
                updatedData: yearData,
                changedField: 'targetLaunchDate',
                productType: 'indirect',
                templateData,
                analysisType,
                contractPriceRanking: contractPriceRanking as Array<{
                  price_rank: number;
                  price: number;
                }>,
                templateType: 'nc',
                supplierName: supplierName,
                yearIndex: tabIndex === 0 ? undefined : tabIndex,
              });
              // Preserve all user-entered values — only fill in what was truly missing
              const existingYearData =
                refData && Object.keys(refData).length > 0
                  ? refData
                  : prev[activePackSizeTab as number]?.[tabIndex] || {};
              const updated = {
                ...prev,
                [activePackSizeTab as number]: {
                  ...prev[activePackSizeTab as number],
                  [tabIndex]: {
                    ...existingYearData,
                    blendedWhlsFee: recalculatedData.blendedWhlsFee,
                    gsmsCost: existingYearData.gsmsCost ?? recalculatedData.gsmsCost,
                    gsmsProfit: existingYearData.gsmsProfit ?? recalculatedData.gsmsProfit,
                    _gsmsProfitManuallySet:
                      existingYearData._gsmsProfitManuallySet ??
                      recalculatedData._gsmsProfitManuallySet,
                    suggestedBidPrice:
                      existingYearData.suggestedBidPrice ?? recalculatedData.suggestedBidPrice,
                    gsmsCostFromMfg:
                      existingYearData.gsmsCostFromMfg ?? recalculatedData.gsmsCostFromMfg,
                    gsmsNetCostFromMfg:
                      existingYearData.gsmsNetCostFromMfg ?? recalculatedData.gsmsNetCostFromMfg,
                  },
                },
              };
              yearFormDataRef.current = updated;
              return updated;
            });
          }, 0);
        }
      }

      // Autosave disabled - user must click Save button manually
      // Trigger save in background if needed
      // if (bidId && !isSaving && !isInitialLoadRef.current) {
      //   setTimeout(async () => {
      //     try {
      //       await handleSaveBidRef.current(true);
      //     } catch (error) {
      //       console.error('Error saving during year tab switch:', error);
      //     }
      //   }, 50);
      // }

      // Load data for the new year tab from ref (same prev we just saved to) so values retain after Copy for all years
      const newYearData = {
        ...(yearFormDataRef.current[activePackSizeTab as number]?.[tabIndex] ||
          yearFormData[activePackSizeTab as number]?.[tabIndex] ||
          {}),
      };
      // ALWAYS recompute contractAnnualRevenue / contractAnnualGP from the year's own
      // primary fields so the very first render after the tab switch shows correct values.
      const ydUsage =
        parseFloat(newYearData.ncEstAnnualUsage || newYearData.targetAnnualUsage || '0') || 0;
      const ydBidPrice = parseFloat(newYearData.suggestedBidPrice || '0') || 0;
      if (ydUsage > 0 && ydBidPrice > 0) {
        newYearData.contractAnnualRevenue = (ydUsage * ydBidPrice).toFixed(2);
        const ydProfit = parseFloat(newYearData.gsmsProfit || '0') || 0;
        if (ydProfit !== 0) {
          newYearData.contractAnnualGP = (ydUsage * ydBidPrice * (ydProfit / 100)).toFixed(2);
        }
      }

      // Persist recomputed revenue/GP into the ref + state so GlobalOutputSection's
      // packSizeFormData aggregation (reads yearFormData state) also has the correct values.
      if (activePackSizeTab !== null && tabIndex !== null && tabIndex !== 5) {
        yearFormDataRef.current = {
          ...yearFormDataRef.current,
          [activePackSizeTab as number]: {
            ...yearFormDataRef.current[activePackSizeTab as number],
            [tabIndex]: newYearData,
          },
        };
        setYearFormData(prev => {
          // Use ref as base (not prev/state) so copy data for other years is not lost
          const refBase =
            yearFormDataRef.current && Object.keys(yearFormDataRef.current).length > 0
              ? yearFormDataRef.current
              : prev;
          return {
            ...refBase,
            [activePackSizeTab as number]: {
              ...refBase[activePackSizeTab as number],
              [tabIndex]: newYearData,
            },
          };
        });
      }

      // Preserve global fields when switching year tabs
      // CRITICAL: Preserve gsmsProfit and _gsmsProfitManuallySet from the loaded year data so that
      // when returning to base year the user's manually set profit % is not overwritten
      // CRITICAL: For NC, always keep targetLaunchDate as the global (base-year) date so Project Information never shows the option-year-specific date.
      setFormData((currentFormData: any) => {
        const merged = preserveGlobalFieldsLogic(newYearData, currentFormData);
        if (
          newYearData._gsmsProfitManuallySet === true &&
          newYearData.gsmsProfit !== null &&
          newYearData.gsmsProfit !== undefined &&
          newYearData.gsmsProfit !== ''
        ) {
          merged.gsmsProfit = newYearData.gsmsProfit;
          merged._gsmsProfitManuallySet = true;
        }
        const baseYearTargetLaunchDate =
          activePackSizeTab != null
            ? yearFormDataRef.current[activePackSizeTab as number]?.[0]?.targetLaunchDate
            : undefined;
        if (baseYearTargetLaunchDate !== undefined && baseYearTargetLaunchDate !== null) {
          merged.targetLaunchDate = baseYearTargetLaunchDate;
        }
        return merged;
      });
    } finally {
      isTabChangingRef.current = false;
    }
  };

  const copyForAllYears = () => {
    if (templateType !== 'nc' || activePackSizeTab === null) return;

    // Access nested structure: yearFormData[activePackSizeTab as number][0]
    // Merge formData (current typed values) with yearFormData[0] (may have computed fields)
    // so we get the most complete base year data including gsmsProfit, ncEstAnnualUsage, etc.
    const latestYearFormData = yearFormDataRef.current ?? yearFormData;
    const baseYearDataFromYearFormData = latestYearFormData[activePackSizeTab as number]?.[0] || {};
    const activePackBaseYearData =
      activeYearTab === 0 && formData && Object.keys(formData).length > 0
        ? { ...baseYearDataFromYearFormData, ...formData }
        : baseYearDataFromYearFormData || formData;

    // Build updated structure — copy base year → option years for ALL pack sizes.
    const prev = yearFormDataRef.current ?? yearFormData;
    let updated = { ...prev };

    const allTabs = allPackSizeTabs?.length > 0 ? allPackSizeTabs : packSizeTabs || [];

    // Check that at least one pack has meaningful data before proceeding.
    const anyPackHasData = allTabs.some((t: { index: number }) => {
      const packRef = yearFormDataRef.current[t.index]?.[0] || {};
      const packBase = packSizeFormData[t.index] || {};
      return Object.keys(packRef).length > 0 || Object.keys(packBase).length > 0;
    });
    if (!anyPackHasData && (!activePackBaseYearData || Object.keys(activePackBaseYearData).length === 0)) {
      toast.error('Base Year data is empty. Please fill Base Year fields first.', {
        autoClose: 5000,
      });
      return;
    }

    // Copy base year → option years for EACH pack.
    for (const packTab of allTabs) {
      const packIndex = packTab.index;
      const isActivePack = packIndex === (activePackSizeTab as number);

      // Resolve base year data for this pack.
      // Priority: packSizeFormData (backend-loaded) → yearFormDataRef (edited) → formData (live, active pack only)
      const packRefYear = yearFormDataRef.current[packIndex]?.[0] || {};
      const packBaseData = packSizeFormData[packIndex] || {};

      let thisPackBaseYear: any;
      if (isActivePack && activeYearTab === 0 && formData && Object.keys(formData).length > 0) {
        thisPackBaseYear = { ...packBaseData, ...packRefYear, ...formData };
      } else {
        thisPackBaseYear = { ...packBaseData, ...packRefYear };
      }

      if (Object.keys(thisPackBaseYear).length === 0) continue;

      // Only copy option years for the active pack — non-active packs retain their existing data
      if (!isActivePack) continue;

      let copiedData = JSON.parse(JSON.stringify(thisPackBaseYear));

      // Ensure ncEstAnnualUsage is computed if missing (from va+dod+ihs+bop)
      const ncEst = parseFloat(copiedData.ncEstAnnualUsage || '0') || 0;
      if (ncEst === 0) {
        const va = parseFloat(copiedData.vaAnnualNcEst || '0') || 0;
        const dod = parseFloat(copiedData.dodAnnualNcEst || '0') || 0;
        const ihs = parseFloat(copiedData.ihsAnnualNcEst || '0') || 0;
        const bop = parseFloat(copiedData.bopAnnualNcEst || '0') || 0;
        const sum = va + dod + ihs + bop;
        if (sum > 0) copiedData = { ...copiedData, ncEstAnnualUsage: sum.toFixed(2) };
      }

      // Ensure pack size is in copied data
      if (packTab?.packSize) {
        copiedData.packSize = packTab.packSize;
        copiedData.gsmsPackSize = packTab.packSize;
      }

      const baseYearBlendedWhlsFee = thisPackBaseYear.blendedWhlsFee;
      const hasBaseYearBlendedWhlsFee =
        baseYearBlendedWhlsFee !== null &&
        baseYearBlendedWhlsFee !== undefined &&
        baseYearBlendedWhlsFee !== '' &&
        baseYearBlendedWhlsFee !== '0.00' &&
        !isNaN(parseFloat(String(baseYearBlendedWhlsFee)));

      const {
        contracts: _c,
        contractsData: _cd,
        analysisDate: _ad,
        targetLaunchDate: _tld,
        analysisType: _at,
        supplierName: _sn,
        blendedWhlsFee: _bwf,
        ...yearSpecificData
      } = copiedData;

      if (
        yearSpecificData.gsmsProfit !== null &&
        yearSpecificData.gsmsProfit !== undefined &&
        yearSpecificData.gsmsProfit !== '' &&
        yearSpecificData.gsmsProfit !== 0
      ) {
        yearSpecificData._gsmsProfitManuallySet = true;
      }

      const baseTargetLaunchDate = thisPackBaseYear.targetLaunchDate || formData.targetLaunchDate;

      // Base year (index 0) for this pack
      const baseYearEntry: any = {
        ...yearSpecificData,
        targetLaunchDate: baseTargetLaunchDate,
        ...(hasBaseYearBlendedWhlsFee && { blendedWhlsFee: String(baseYearBlendedWhlsFee) }),
      };
      const baseUsage =
        parseFloat(baseYearEntry.ncEstAnnualUsage || baseYearEntry.targetAnnualUsage || '0') || 0;
      const baseBidPrice = parseFloat(baseYearEntry.suggestedBidPrice || '0') || 0;
      const baseProfit = parseFloat(baseYearEntry.gsmsProfit || '0') || 0;
      if (baseUsage > 0 && baseBidPrice > 0) {
        const baseRevenue = baseUsage * baseBidPrice;
        baseYearEntry.contractAnnualRevenue = baseRevenue.toFixed(2);
        baseYearEntry.contractAnnualGP = (baseRevenue * (baseProfit / 100)).toFixed(2);
      }

      updated = {
        ...updated,
        [packIndex]: {
          ...updated[packIndex],
          0: baseYearEntry,
        },
      };

      // Option years 1-4 for this pack
      for (let i = 1; i <= 4; i++) {
        let yearSpecificTargetLaunchDate = baseTargetLaunchDate;
        if (baseTargetLaunchDate && i > 0) {
          try {
            const baseDate =
              typeof baseTargetLaunchDate === 'string'
                ? new Date(baseTargetLaunchDate)
                : baseTargetLaunchDate;
            if (baseDate instanceof Date && !isNaN(baseDate.getTime())) {
              const yearDate = new Date(baseDate);
              yearDate.setFullYear(baseDate.getFullYear() + i);
              yearSpecificTargetLaunchDate = yearDate;
            }
          } catch (_e) {
            /* ignore */
          }
        }

        const {
          _gsmsProfitManuallySet: _stripFlag,
          gsmsProfit: _stripProfit,
          _gsmsCostFromApi: _stripCostFromApi,
          ...yearSpecificDataForOptionYear
        } = yearSpecificData;

        const yearData: any = {
          ...formData,
          ...packBaseData,
          ...yearSpecificDataForOptionYear,
          targetLaunchDate: yearSpecificTargetLaunchDate,
          _gsmsProfitManuallySet: false,
          _gsmsCostFromApi: false,
          supplierDiscount:
            thisPackBaseYear.supplierDiscount ??
            packBaseData.supplierDiscount ??
            formData.supplierDiscount ??
            0,
          supplierCode:
            thisPackBaseYear.supplierCode ?? packBaseData.supplierCode ?? formData.supplierCode ?? '',
          gsmsPackSize: packTab?.packSize
            ? String(packTab.packSize)
            : (yearSpecificDataForOptionYear.gsmsPackSize ??
              packBaseData.gsmsPackSize ??
              formData.gsmsPackSize),
        };

        const suggestedBidPrice = parseFloat(yearSpecificData.suggestedBidPrice || '0') || 0;
        const vaAnnualNcEst = parseFloat(yearSpecificData.vaAnnualNcEst || '0') || 0;
        const dodAnnualNcEst = parseFloat(yearSpecificData.dodAnnualNcEst || '0') || 0;
        const ihsAnnualNcEst = parseFloat(yearSpecificData.ihsAnnualNcEst || '0') || 0;
        const bopAnnualNcEst = parseFloat(yearSpecificData.bopAnnualNcEst || '0') || 0;

        let optionYearBlendedWhlsFee: number | null = null;
        if (suggestedBidPrice > 0 && yearSpecificTargetLaunchDate) {
          try {
            optionYearBlendedWhlsFee = whlsFeeCalculationNC(
              yearSpecificTargetLaunchDate,
              suggestedBidPrice,
              vaAnnualNcEst,
              dodAnnualNcEst,
              ihsAnnualNcEst,
              bopAnnualNcEst
            );
            yearData.blendedWhlsFee = optionYearBlendedWhlsFee.toFixed(2);
          } catch {
            /* ignore */
          }
        }

        let finalYearData: any = { ...yearData };

        const baseGsmsCost = parseFloat(yearSpecificData.gsmsCost || '0') || 0;
        const baseBlendedWhlsFee = parseFloat(String(baseYearBlendedWhlsFee ?? '0')) || 0;

        if (suggestedBidPrice > 0 && baseGsmsCost > 0) {
          const optBlendedWhlsFee =
            optionYearBlendedWhlsFee !== null ? optionYearBlendedWhlsFee : baseBlendedWhlsFee;
          const optGsmsCost = baseGsmsCost - baseBlendedWhlsFee + optBlendedWhlsFee;
          const optGsmsProfit = ((suggestedBidPrice - optGsmsCost) / suggestedBidPrice) * 100;
          finalYearData = {
            ...finalYearData,
            gsmsCost: optGsmsCost.toFixed(2),
            gsmsProfit: optGsmsProfit.toFixed(2),
            _gsmsProfitManuallySet: false,
          };
        } else if (
          yearSpecificData.gsmsProfit !== undefined &&
          yearSpecificData.gsmsProfit !== null &&
          yearSpecificData.gsmsProfit !== ''
        ) {
          finalYearData = {
            ...finalYearData,
            gsmsProfit: String(yearSpecificData.gsmsProfit),
            _gsmsProfitManuallySet: false,
          };
        }

        if (
          yearSpecificData.ncEstAnnualUsage !== undefined &&
          yearSpecificData.ncEstAnnualUsage !== null
        ) {
          finalYearData.ncEstAnnualUsage = String(yearSpecificData.ncEstAnnualUsage);
        }

        const optUsage =
          parseFloat(finalYearData.ncEstAnnualUsage || finalYearData.targetAnnualUsage || '0') || 0;
        const optBidPrice = parseFloat(finalYearData.suggestedBidPrice || '0') || suggestedBidPrice;
        const optProfit = parseFloat(finalYearData.gsmsProfit || '0') || 0;
        if (optUsage > 0 && optBidPrice > 0) {
          const optRevenue = optUsage * optBidPrice;
          finalYearData.contractAnnualRevenue = optRevenue.toFixed(2);
          finalYearData.contractAnnualGP = (optRevenue * (optProfit / 100)).toFixed(2);
        }

        updated = {
          ...updated,
          [packIndex]: {
            ...updated[packIndex],
            [i]: finalYearData,
          },
        };
      }
    } // end for each packTab

    // CRITICAL: Set ref FIRST (synchronously) so 5-Year Summary sees correct data immediately
    yearFormDataRef.current = updated;

    // Store copy snapshot in dedicated ref so calculate5YearSummary can always read
    // option years 1-4 from it, unaffected by any React state timing or useEffect sync.
    copyAllYearsDataRef.current = updated;

    setYearFormData(updated);

    // CRITICAL: If user is on base year tab for active pack, update formData to reflect preserved blendedWhlsFee
    // Always update formData with the active year's copied data so the active row
    // in NCGlobalOutputTable immediately reflects the copy (not stale formData).
    const activeYearCopiedData = updated[activePackSizeTab as number]?.[activeYearTab as number];
    if (activeYearCopiedData && Object.keys(activeYearCopiedData).length > 0) {
      setFormData((prev: any) => ({
        ...prev,
        ...activeYearCopiedData,
      }));
    }

    toast.success(
      'Base Year data copied to all option years. 5-Year Summary will reflect the updated values.',
      { autoClose: 4000 }
    );
  };

  const handleMarketShareClick = async (vendor: string) => {
    // Cancel contract price ranking request when switching companies
    if (contractPriceRankingAbortControllerRef.current) {
      contractPriceRankingAbortControllerRef.current.abort();
      contractPriceRankingAbortControllerRef.current = null;
    }

    // Get date range from MobX store (stored when Apply Filter is clicked)
    // This is the "applied" filter that should always be used for Market Share API calls
    const currentData = packSizeFormData[activePackSizeTab as number] || formData;
    let startDate: Date | null | undefined = undefined;
    let endDate: Date | null | undefined = undefined;

    if (bidId && currentData.conceptName && currentData.packSize) {
      const bidIdNum = parseInt(bidId);
      const concept = currentData.conceptName;
      const packSize = parseFloat(currentData.packSize);
      const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);

      // Always use stored date range if it exists (even if null - means filter was cleared)
      // The stored date range is what was applied via Apply Filter button
      if (storedDateRange !== undefined) {
        // Use stored date range (this is the applied filter)
        startDate = storedDateRange.startDate;
        endDate = storedDateRange.endDate;
      } else if (chartDateRange.startDate && chartDateRange.endDate) {
        // Fall back to current chart date range if nothing stored yet
        startDate = chartDateRange.startDate;
        endDate = chartDateRange.endDate;
      }
    }

    if (selectedCompany === vendor) {
      // If clicking the same company, deselect it (revert to overall)
      setSelectedCompany(null);
      // Keep date range - don't reset it. Only refresh dependent charts (Volume by COT, Market Sales Trends).
      await loadMarketSalesTrendData(null, false, startDate, endDate);
      await loadVolumeByCot(null, startDate, endDate);
    } else {
      // Select the new company. Only refresh dependent charts, not Market Share by Volume.
      setSelectedCompany(vendor);
      await loadMarketSalesTrendData(vendor, false, startDate, endDate);
      await loadVolumeByCot(vendor, startDate, endDate);
    }
  };

  const handleFieldKeyDown = (fieldName: string, e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    // Mark this field as Tab-pressed so the natural onBlur event bypasses shouldSkipRecalc.
    // We do NOT call handleFieldBlur here — PrimeReact will fire props.onBlur after Tab
    // processing (with the correctly formatted event.target.value), which is the proven path.
    lastTabFieldRef.current = fieldName;
  };

  return {
    handleFieldBlur,
    handleFieldKeyDown,
    replicateAcrossPackSizes,
    handlePackSizeTabChange,
    handleYearTabChange,
    copyForAllYears,
    handleMarketShareClick,
    isTabChangingRef,
  };
}
